import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Token storage --------------------------------------------------------------
const ACCESS_KEY = "essenze_admin_access";
const REFRESH_KEY = "essenze_admin_refresh";

export const getAccessToken = () => {
  try {
    return localStorage.getItem(ACCESS_KEY) || "";
  } catch {
    return "";
  }
};

export const setTokens = ({ access_token, refresh_token } = {}) => {
  try {
    if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  } catch {
    /* ignore */
  }
};

export const clearTokens = () => {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
};

// Axios instance -------------------------------------------------------------
// withCredentials kept on for cookie fallback on same-origin browsers.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Authorization header automatically.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Auto-refresh on 401 + redirect to login on hard failure.
let refreshPromise = null;
let onUnauthorizedHandler = null;

export const setOnUnauthorized = (fn) => {
  onUnauthorizedHandler = fn;
};

const tryRefresh = async () => {
  if (!refreshPromise) {
    const refresh_token = localStorage.getItem(REFRESH_KEY) || "";
    refreshPromise = axios
      .post(
        `${API}/auth/refresh`,
        refresh_token ? { refresh_token } : {},
        { withCredentials: true },
      )
      .then((r) => {
        const newAccess = r?.data?.access_token;
        if (newAccess) localStorage.setItem(ACCESS_KEY, newAccess);
        return newAccess;
      })
      .catch((err) => {
        clearTokens();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // Don't retry login/refresh themselves
    const url = originalRequest.url || "";
    const isAuthRoute = url.includes("/auth/login") || url.includes("/auth/refresh");

    if (status === 401 && !originalRequest._retried && !isAuthRoute) {
      originalRequest._retried = true;
      try {
        const newAccess = await tryRefresh();
        if (newAccess) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          return api(originalRequest);
        }
      } catch (_) {
        // refresh failed → fall through to logout flow
      }
      // Refresh failed: signal app to redirect to login
      clearTokens();
      if (typeof onUnauthorizedHandler === "function") {
        onUnauthorizedHandler();
      }
    }

    return Promise.reject(error);
  },
);

// Public ---------------------------------------------------------------------
export const fetchPerfumes = async (params = {}) => {
  const { data } = await api.get("/perfumes", { params });
  return data;
};

export const fetchPerfume = async (id) => {
  const { data } = await api.get(`/perfumes/${id}`);
  return data;
};

// Admin ----------------------------------------------------------------------
export const adminLogin = async (email, password) => {
  const { data } = await api.post("/auth/login", { email, password });
  setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
  return data;
};

export const adminLogout = async () => {
  try {
    await api.post("/auth/logout");
  } catch {
    /* ignore */
  }
  clearTokens();
};

export const adminMe = async () => {
  const { data } = await api.get("/auth/me");
  return data;
};

export const adminListAll = async () => {
  const { data } = await api.get("/perfumes/admin/all");
  return data;
};

export const adminUpdatePerfume = async (id, payload) => {
  const { data } = await api.put(`/perfumes/${id}`, payload);
  return data;
};

export const adminToggleVisibility = async (id, visible) => {
  const { data } = await api.patch(`/perfumes/${id}/visibility`, { visible });
  return data;
};

export const adminDeletePerfume = async (id) => {
  await api.delete(`/perfumes/${id}`);
};

export const adminCreatePerfume = async (payload = {}) => {
  const { data } = await api.post("/perfumes", payload);
  return data;
};
